import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faClose, faCoins } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { EResellerCommissionDurationType } from "../../Enum/EResellerCommissionDurationType"
import GetSpace from "../../utils/space"
import { ResellerCommissionPlan } from "../../Objects/AdminReseller/ResellerCommissionPlan"
import { SaveResellerCommissionPlanRequest } from "../../hooks/AdminDashboard/useAdminReseller"

interface ResellerCommissionPlanPopupProps {
  plan: ResellerCommissionPlan | null
  onClose: () => void
  onSave: (planId: number | null, request: SaveResellerCommissionPlanRequest) => void
}

export const ResellerCommissionPlanPopup: React.FC<ResellerCommissionPlanPopupProps> = ({ plan, onClose, onSave }) => {
  const { t } = useTranslation("admin")

  const [name, setName] = useState(plan?.name ?? "")

  const [commissionPercentage, setCommissionPercentage] = useState(String(plan?.commissionPercentage ?? "10"))

  const [durationType, setDurationType] = useState<EResellerCommissionDurationType>(
    plan?.durationType ?? EResellerCommissionDurationType.Recurring,
  )

  const [durationInMonths, setDurationInMonths] = useState(String(plan?.durationInMonths ?? "12"))

  const [isActive, setIsActive] = useState(plan?.isActive ?? true)

  const [isDefault, setIsDefault] = useState(plan?.isDefault ?? false)

  const durationTypeOptions = [
    {
      label: t("reseller_commission_plan_duration_recurring"),
      value: String(EResellerCommissionDurationType.Recurring),
    },
    {
      label: t("reseller_commission_plan_duration_fixed_months"),
      value: String(EResellerCommissionDurationType.FixedMonths),
    },
  ]

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    onSave(plan?.id ?? null, {
      name,
      commissionPercentage: Number(commissionPercentage),
      durationType,
      durationInMonths:
        durationType === EResellerCommissionDurationType.FixedMonths ? Number(durationInMonths) : undefined,
      isActive,
      isDefault,
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={plan ? t("reseller_commission_plan_edit_title") : t("reseller_commission_plan_create_title")}
              icon={faCoins}
              iconColor={THEME.colors.iconBGColor}
              children={
                <>
                  <GenericDropDown
                    data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                    initialState={isActive ? EEnableStatusType.Active : EEnableStatusType.Inactive}
                    handlerFuncion={(e) => setIsActive((e.target.selectedIndex as EEnableStatusType) === EEnableStatusType.Active)}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.select.status"
                  />

                  <GetSpace left={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.button.onClose"
                  />
                </>
              }
            />

            <form onSubmit={handleSubmit}>
              <div className="space-y-3 p-2">
                <GenericDropDown
                  data={durationTypeOptions}
                  initialState={String(durationType)}
                  handlerFuncion={(e) => setDurationType(Number(e.target.value) as EResellerCommissionDurationType)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.select.durationType"
                />

                <InputField
                  value={name}
                  type="text"
                  placeholder={t("reseller_commission_plan_name_placeholder")}
                  onChange={(e) => setName(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.input.name"
                />

                <InputField
                  value={commissionPercentage}
                  type="number"
                  placeholder={t("reseller_commission_plan_percentage_placeholder")}
                  onChange={(e) => setCommissionPercentage(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.input.commissionPercentage"
                />

                {durationType === EResellerCommissionDurationType.FixedMonths && (
                  <InputField
                    value={durationInMonths}
                    type="number"
                    placeholder={t("reseller_commission_plan_duration_months_placeholder")}
                    onChange={(e) => setDurationInMonths(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.input.durationInMonths"
                  />
                )}

                <SimpleToggle
                  title={t("reseller_commission_plan_is_default")}
                  value={isDefault}
                  onChange={setIsDefault}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.toggle.isDefault"
                />
              </div>

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                <Button
                  typeButton="submit"
                  label={t("reseller_commission_plan_save")}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.resellerCommissionPlanPopup.button.save"
                />
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
